import React, { type ReactNode, useCallback } from 'react';

import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { CSV_MAPPING_SUPPORT_LINK } from '../../../common/constants';
import { LearnMore, PageDescription, PageHeading } from '../../../common/ui';
import { CloseAndReturnButton } from '../../../common/ui/close-and-return-button';
import { useProjectSpecificContent } from '../../../common/utils/project-specific-content';
import { useProjectData, useSource } from '../../../controllers/import-state/selectors';
import { useCancelImportModalState } from '../../../controllers/ui-state/selectors/cancel-import-modal';

type Props = {
	onCloseAndReturnToUploadStep: () => void;
	isCompactHeader?: boolean;
	offsetModalY?: number;
};

export const Header = ({ onCloseAndReturnToUploadStep, isCompactHeader, offsetModalY }: Props) => {
	const { formatMessage } = useIntl();
	const [{ type }] = useProjectData();
	const [source] = useSource();
	const {
		mappingHeading,
		mappingDescription,
		mappingDescriptionWithLink,
		mappingLearnMore,
		closeButtonLabel,
	} = useProjectSpecificContent(type, source);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, { setCancelImportModalState }] = useCancelImportModalState();

	const learnMoreLinkClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'x2cLearnMoreToMapCsvLink',
			}),
		);
	}, [createAnalyticsEvent]);

	const handleOpenCancelImport = useCallback(() => {
		setCancelImportModalState({
			isCancelImportModalOpen: true,
			shouldCloseDrawerOnCancel: false,
			onCloseModalCallback: onCloseAndReturnToUploadStep,
			offsetModalY,
		});
	}, [offsetModalY, onCloseAndReturnToUploadStep, setCancelImportModalState]);

	if (isCompactHeader) {
		return (
			<Stack>
				<Flex justifyContent="space-between">
					<PageHeading>{formatMessage(mappingHeading)}</PageHeading>
					<IconButton
						icon={CrossIcon}
						appearance="subtle"
						label={formatMessage(closeButtonLabel)}
						onClick={handleOpenCancelImport}
					/>
				</Flex>
				<PageDescription data-testid="project-create-importer-upload-flow.ui.mapping-screen.header.compact-description">
					{formatMessage(mappingDescriptionWithLink, {
						link: (content: ReactNode) => (
							<Box as="a" href={CSV_MAPPING_SUPPORT_LINK} target="_blank">
								{content}
							</Box>
						),
					})}
				</PageDescription>
			</Stack>
		);
	}

	return (
		<Box as="div" xcss={headingStyles}>
			<CloseAndReturnButton onCloseAndReturnToUploadStep={onCloseAndReturnToUploadStep} />
			<PageHeading>{formatMessage(mappingHeading)}</PageHeading>
			<PageDescription data-testid="project-create-importer-upload-flow.ui.mapping-screen.header.description">
				{formatMessage(mappingDescription, {
					link: (
						<LearnMore href={CSV_MAPPING_SUPPORT_LINK} target="_blank" onClick={learnMoreLinkClick}>
							{formatMessage(mappingLearnMore)}
						</LearnMore>
					),
				})}
			</PageDescription>
		</Box>
	);
};

const headingStyles = xcss({
	marginBlockEnd: 'space.300',
	flex: '0 1 auto',
});
